import {FormControl, FormField, RadioOption, Separator} from 'platform/components';
import {Heading, Space, VStack, Grid, GridItem, Show} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {useAccM2StrediskoGetCentresQuery} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {useVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';

const parametersForVatOptions = [
  {
    label: i18n.t('entity.warehouse.labels.vatDeductible'),
    value: 'true',
  },
  {
    label: i18n.t('entity.warehouse.labels.nonVatDeductible'),
    value: 'false',
  },
];

interface AccountingTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId: string | Nullish;
}

export function AccountingTab(props: AccountingTabProps) {
  const {data: serviceCentres, isLoading: isLoadingServiceCentres} =
    useAccM2StrediskoGetCentresQuery();

  const serviceCentresOptions =
    serviceCentres?.m2centres?.map((centre) => ({
      label: centre?.name ?? '',
      value: centre?.code ?? '',
    })) ?? [];

  const excludedServiceCenterIds =
    useWatch({control: props.formApi.control, name: 'accounting.excludedServiceCenterIds'}) ?? [];

  const filteredServiceCentresOptions =
    serviceCentresOptions?.filter((option) => !excludedServiceCenterIds.includes(option.value)) ??
    [];

  const [vatRatesOptions] = useVatRatesOptions();

  const vatOptions = vatRatesOptions.map((vat) => ({
    label: vat.label,
    value: vat.value,
  }));

  const isTaxDeductible = useWatch({control: props.control, name: 'accounting.isTaxDeductible'});

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <FormField
            control={props.control}
            name="accounting.isTaxDeductible"
            type="radio"
            label={i18n.t('entity.warehouse.labels.parametersForVat')}
            options={parametersForVatOptions as RadioOption[]}
          />
          <Show when={isTaxDeductible === 'false'}>
            <Grid columns={2}>
              <GridItem span={1}>
                <FormField
                  isRequired
                  isNotClearable
                  control={props.control}
                  name="accounting.fixedVatType"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.fixedVat')}
                  options={vatOptions}
                />
              </GridItem>
            </Grid>
          </Show>
          <Separator spacing={0} />
          <Heading size={4}>{i18n.t('entity.warehouse.labels.serviceCenters')}</Heading>
          <FormField
            control={props.control}
            name="accounting.excludedServiceCenterIds"
            type="multiChoice"
            label={i18n.t('entity.warehouse.labels.excludeServiceCenters')}
            options={serviceCentresOptions}
            isLoading={isLoadingServiceCentres}
          />

          <Grid columns={2} align="flex-end">
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="accounting.defaultServiceCenterId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.defaultServiceCenter')}
                options={filteredServiceCentresOptions}
                isLoading={isLoadingServiceCentres}
              />
            </GridItem>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="accounting.isDefaultServiceCenterLocked"
                type="checkbox"
                label={i18n.t('entity.warehouse.labels.cannotBeChanged')}
              />
            </GridItem>
          </Grid>
        </VStack>
      </SettingsSection>

      <Space vertical={18} />

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </>
  );
}
